import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/api/axiosInstance'

export const SERVICE_REQUESTS_PATH = '/service-requests/'

const QUERY_KEYS = {
    requests: ['service-requests'] as const,
    nearbyRequests: (lat: number, lng: number, radius: number) => ['service-requests', 'nearby', lat, lng, radius] as const,
    request: (id: number) => ['service-requests', id] as const,
}

export const useServiceRequests = () => {
    return useQuery({
        queryKey: QUERY_KEYS.requests,
        queryFn: async () => {
            const response = await axiosInstance.get(SERVICE_REQUESTS_PATH)
            const data = response.data.results || response.data
            return data
        },
        staleTime: 2 * 60 * 1000,
    })
}

export const useServiceRequest = (id: number) => {
    return useQuery({
        queryKey: QUERY_KEYS.request(id),
        queryFn: async () => {
            const response = await axiosInstance.get(`${SERVICE_REQUESTS_PATH}${id}/`)
            return response.data
        },
        enabled: !!id,
        staleTime: 2 * 60 * 1000,
    })
}

export const useNearbyServiceRequests = (latitude?: number, longitude?: number, radius: number = 5000) => {
    return useQuery({
        queryKey: QUERY_KEYS.nearbyRequests(latitude || 0, longitude || 0, radius),
        queryFn: async () => {
            if (!latitude || !longitude) return []
            const response = await axiosInstance.get(
                `${SERVICE_REQUESTS_PATH}nearby/?latitude=${latitude}&longitude=${longitude}&radius=${radius}`
            )
            const data = response.data.results || response.data
            return data
        },
        enabled: !!latitude && !!longitude,
        staleTime: 1 * 60 * 1000,
        refetchInterval: 30 * 1000,
    })
}

export const useCreateServiceRequest = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data: any) => {
            const response = await axiosInstance.post(SERVICE_REQUESTS_PATH, data)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
            // Invalidate available schedule so blocked hours update immediately
            queryClient.invalidateQueries({ queryKey: ['available-schedule'] })
        },
    })
}

export const useAcceptServiceRequest = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (requestId: number) => {
            const response = await axiosInstance.post(`${SERVICE_REQUESTS_PATH}${requestId}/accept/`)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
        },
    })
}

export const useApproveServiceRequest = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (requestId: number) => {
            const response = await axiosInstance.post(`${SERVICE_REQUESTS_PATH}${requestId}/approve/`)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
        },
    })
}

export const useDenyServiceRequest = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ requestId, reason }: { requestId: number; reason: string }) => {
            const response = await axiosInstance.post(`${SERVICE_REQUESTS_PATH}${requestId}/deny/`, { reason })
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
        },
    })
}

export const useCancelServiceRequest = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ requestId, reason }: { requestId: number; reason: string }) => {
            const response = await axiosInstance.post(`${SERVICE_REQUESTS_PATH}${requestId}/cancel/`, { reason })
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
        },
    })
}

export const useStartService = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (requestId: number) => {
            const response = await axiosInstance.post(`${SERVICE_REQUESTS_PATH}${requestId}/start/`)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
        },
    })
}

export const useCompleteService = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (requestId: number) => {
            const response = await axiosInstance.post(`${SERVICE_REQUESTS_PATH}${requestId}/complete/`)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
        },
    })
}

export const useConfirmSatisfaction = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ requestId, satisfied }: { requestId: number; satisfied: boolean }) => {
            const response = await axiosInstance.post(`${SERVICE_REQUESTS_PATH}${requestId}/confirm_satisfaction/`, { satisfied })
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.requests })
        },
    })
}


